import { useState } from "react";
import { Link } from "react-router-dom";
import styles from "./contactus.module.css";
import { MapPin, Mail, Phone, User, MessageSquare } from "react-feather";
import { Controller, useForm } from "react-hook-form";
import { TextField, InputAdornment, Alert, Snackbar } from "@mui/material";
import Loader from "../../../admin/components/Loader";
import axios from "axios";
import { useWebsite } from "../../../context/WebsiteContext";
import { useSiteBranding } from "../../../hooks/useSiteBranding";

const ContactUs = () => {
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");

  const { websiteData, loading: wLoading } = useWebsite();

  useSiteBranding("Contact Us", websiteData);

  const themePrimaryColor = websiteData?.home_config?.primaryColor || "#ef233c";
  const themeSecondaryColor = websiteData?.home_config?.secondaryColor || "#0a2a52";
  const companyName = websiteData?.home_config?.companyName || "EF LOGISTICS";
  const logoURL = websiteData?.home_config?.logoURL || "/images/logo.png";
  const companyAddress = websiteData?.home_config?.address || "International House, 142 Cromwell Road, London, SW7 4EF";
  const companyEmail = websiteData?.home_config?.contactEmail || "admin@eflogistics.io";
  const companyPhone = websiteData?.home_config?.phone || "+44 20 0000 0000";

  const { handleSubmit, control, reset, formState: { errors } } = useForm({
    mode: "onChange",
  });

  const onSubmit = (data) => {
    setLoading(true);
    data = { ...data, utoken: websiteData ? websiteData?.utoken : '' };
    axios.post(process.env.REACT_APP_API_URL + "v1/contact-us", data).then((response) => {
      setLoading(false);
      if (response.data.status === "SUCCESS") {
        reset({ firstname: "", lastname: "", phone: "", email: "", message: "" });
        setSnackbarOpen(true);
        setSnackbarType("success");
        setSnackbarMessage(response.data.message);
      }
    }).catch((error) => {
      setLoading(false);
    });
  };

  return (<>
    {(loading || wLoading) && <Loader />}

    <div
      className={styles.PageWrap}
      style={{ "--primary-color": themePrimaryColor, "--secondary-color": themeSecondaryColor }}
    >
      <div className={styles.Container}>
        {/* BRAND HERO */}
        <div className={styles.BrandHero}>
          <div className={styles.HeroTopBar}>
            <Link to="/" className={styles.BackHomeBtn}>← Back to Home</Link>
            <div className={styles.HeroChip}>We&apos;re Here to Help</div>
          </div>

          <div className={styles.BrandHeroContent}>
            <div className={styles.BrandLeft}>
              <div>
                <img src={logoURL} alt={companyName} className={styles.BrandLogo} />
              </div>

              <div className={styles.BrandText}>
                <h2>{companyName}</h2>
                <p>Reach out anytime — our team typically responds within 24 hours.</p>
              </div>
            </div>

            <div className={styles.BrandRight}>
              <h1>Contact Us</h1>
              <p>We&apos;d love to hear from you. Send us a message and our team will get back to you shortly, or reach us directly using the details below.</p>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.contactpg}>
        <div className={styles.Container}>
          <div className={styles.ContactCard}>
            <div className={styles.ContactFromRow}>

              <div className={styles.ContactFormBigSec}>
                <h2 className={styles.FormTitle}>Send Us A Message</h2>
                <p className={styles.FormSubText}>Fill out the form below and our team will get back to you shortly.</p>

                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className={styles.FormRow}>
                    <div className={styles.FormFieldHalf}>
                      <Controller
                        name="firstname"
                        control={control}
                        defaultValue=""
                        rules={{ required: "Please enter first name" }}
                        render={({ field }) => (<TextField
                          {...field}
                          classes={{ root: styles.inputRoot }}
                          fullWidth
                          label="First Name *"
                          variant="outlined"
                          error={!!errors?.firstname}
                          InputProps={{
                            startAdornment: (<InputAdornment position="start"><User size={18} /></InputAdornment>)
                          }}
                        />)}
                      />
                      {errors?.firstname && (<span className={styles.ErrorM}>{errors?.firstname?.message}</span>)}
                    </div>

                    <div className={styles.FormFieldHalf}>
                      <Controller
                        name="lastname"
                        control={control}
                        defaultValue=""
                        rules={{ required: "Please enter last name" }}
                        render={({ field }) => (<TextField
                          {...field}
                          classes={{ root: styles.inputRoot }}
                          fullWidth
                          label="Last Name *"
                          variant="outlined"
                          error={!!errors?.lastname}
                          InputProps={{
                            startAdornment: (<InputAdornment position="start"><User size={18} /></InputAdornment>)
                          }}
                        />)}
                      />
                      {errors?.lastname && (<span className={styles.ErrorM}>{errors?.lastname?.message}</span>)}
                    </div>

                    <div className={styles.FormFieldHalf}>
                      <Controller
                        name="phone"
                        control={control}
                        defaultValue=""
                        rules={{ required: "Please enter phone no" }}
                        render={({ field }) => (<TextField
                          {...field}
                          classes={{ root: styles.inputRoot }}
                          fullWidth
                          label="Phone Number *"
                          variant="outlined"
                          error={!!errors?.phone}
                          InputProps={{
                            startAdornment: (<InputAdornment position="start"><Phone size={18} /></InputAdornment>)
                          }}
                        />)}
                      />
                      {errors?.phone && (<span className={styles.ErrorM}>{errors?.phone?.message}</span>)}
                    </div>

                    <div className={styles.FormFieldHalf}>
                      <Controller
                        name="email"
                        control={control}
                        defaultValue=""
                        rules={{
                          required: "Please enter email",
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address",
                          }
                        }}
                        render={({ field }) => (<TextField
                          {...field}
                          classes={{ root: styles.inputRoot }}
                          fullWidth
                          label="Email *"
                          variant="outlined"
                          error={!!errors?.email}
                          InputProps={{
                            startAdornment: (<InputAdornment position="start"><Mail size={18} /></InputAdornment>)
                          }}
                        />)}
                      />
                      {errors?.email && (<span className={styles.ErrorM}>{errors?.email?.message}</span>)}
                    </div>

                    <div className={styles.FormFieldFull}>
                      <Controller
                        name="message"
                        control={control}
                        defaultValue=""
                        rules={{ required: "Please enter your query" }}
                        render={({ field }) => (<TextField
                          {...field}
                          classes={{ root: styles.inputRoot }}
                          fullWidth
                          multiline
                          minRows={4}
                          label="Message *"
                          variant="outlined"
                          error={!!errors?.message}
                          InputProps={{
                            startAdornment: (<InputAdornment position="start" sx={{ alignSelf: "flex-start", marginTop: "14px" }}><MessageSquare size={18} /></InputAdornment>)
                          }}
                        />)}
                      />
                      {errors?.message && (<span className={styles.ErrorM}>{errors?.message?.message}</span>)}
                    </div>

                    <div className={styles.FormFieldFull}>
                      <button type="submit" className={styles.SubButton} disabled={loading}>
                        {loading ? "Submitting..." : "Submit Message"}
                      </button>
                    </div>
                  </div>
                </form>
              </div>

              <div className={styles.ContactFormSmallSec}>
                <h3 className={styles.InfoTitle}>Contact Information</h3>
                <p className={styles.InfoSubText}>Reach out to us directly through any of the channels below.</p>

                <div className={styles.InfoItem}>
                  <span className={styles.InfoIcon}><MapPin size={20} /></span>
                  <div>
                    <p className={styles.InfoLabel}>Address</p>
                    <a
                      className={styles.InfoValue}
                      rel="noreferrer"
                      href={`https://www.google.com/maps/search/${encodeURIComponent(companyAddress)}`}
                      target="_blank"
                    >
                      {companyAddress}
                    </a>
                  </div>
                </div>

                <div className={styles.InfoItem}>
                  <span className={styles.InfoIcon}><Mail size={20} /></span>
                  <div>
                    <p className={styles.InfoLabel}>Email</p>
                    <a className={styles.InfoValue} href={`mailto:${companyEmail}`}>{companyEmail}</a>
                  </div>
                </div>

                <div className={styles.InfoItem}>
                  <span className={styles.InfoIcon}><Phone size={20} /></span>
                  <div>
                    <p className={styles.InfoLabel}>Phone</p>
                    <a className={styles.InfoValue} href={`tel:${companyPhone}`}>{companyPhone}</a>
                  </div>
                </div>
              </div>

            </div>

            <div className={styles.mapWrap}>
              <iframe title="contact map" src={`https://www.google.com/maps?q=${encodeURIComponent(companyAddress)}&output=embed`} width="100%" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.CopyrightBar}>
        <p className={styles.CopyrightText}>&copy; {new Date().getFullYear()} {companyName}. All rights reserved.</p>
      </div>
    </div>

    <Snackbar
      open={snackbarOpen}
      autoHideDuration={6000}
      onClose={(e) => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Alert
        onClose={(e) => setSnackbarOpen(false)}
        severity={snackbarType}
        variant="filled"
        sx={{ width: "100%" }}
      >
        {snackbarMessage}
      </Alert>
    </Snackbar>
  </>);
};

export default ContactUs;